import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';

import { SlidingPane, Align } from '@holmescorp/base-widgets/sliding-pane';

import { publish, subscribe } from 'pub-sub';
import { registry, lccstate } from 'lcc-state';
import { delay } from '@holmescorp/common';
import { viewport } from '../support/viewport';

import css from '../css/heading.m.css';

interface MobileNavPaneIcache {
	open: boolean;
	aboutToOpen: boolean;
}

interface MobileNavPaneProperties {
	onClose?: () => void;
}

const icache = createICacheMiddleware<MobileNavPaneIcache>();
const factory = create({
	icache,
	theme,
	lccstate
}).properties<MobileNavPaneProperties>();

export const MobileNavPane = factory(({ properties, middleware: { icache, theme, lccstate } }) => {
	const { onClose } = properties();
	icache.getOrSet('aboutToOpen', false);
	const open = icache.getOrSet('open', () => {
		subscribe('mobile-nav/open', async () => {
			icache.set('aboutToOpen', true);
			await delay(200);
			icache.set('open', true);
		});
		subscribe('close-all/panes', () => {
			icache.set('open', false);
		});
		return false;
	});

	const { get, path } = lccstate;

	const { productname } = get(path('staticprops'));
	const themedCss = theme.classes(css);
	const { vw } = viewport();
	const xqa = `mobile-nav-pane-${open}`;

	return w(
		SlidingPane,
		{
			xqa,
			key: 'settings-pane',
			open,
			align: 'right' as Align,
			underlay: true,
			width: Math.min(vw, 1024),
			title: productname,
			closeText: 'Close',
			onRequestClose: () => {
				icache.set('open', false);
				icache.set('aboutToOpen', async () => {
					await delay(500);
					return true;
				});
				publish('mobile-nav/close');
				onClose && onClose();
			}
		},
		[
			v('ul', { classes: [themedCss.hamburgerMenu] }, [
				v(
					'li',
					{
						key: 'mobile-nav-home-link',
						xqa: 'mobile-nav-home-link',
						tabIndex: 0,
						classes: [themedCss.hamburgerMenuItem],
						onclick: (event: MouseEvent) => {
							event.stopPropagation();
							publish('site/homepage');
						}
					},
					['Home']
				),
				v(
					'li',
					{
						key: 'mobile-nav-my-progress-link',
						xqa: 'mobile-nav-my-progress-link',
						tabIndex: 1,
						classes: [themedCss.hamburgerMenuItem],
						onclick: (event: MouseEvent) => {
							event.stopPropagation();
							publish('site/progress');
						}
					},
					['My Progress']
				)
			])
		]
	);
});

registry.define('MobileNavPane', MobileNavPane);
